import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import type { Colleague } from '../lib/transfer'
import { transferredFromName } from '../lib/transferredFrom'
import type { CounterView, Permission, Ticket } from '../lib/types'
import { useStaffText } from './StaffText'

/** "Transferred · D2 · Anna", the counter's Queue information chip; nothing when nobody sent the ticket. */
export function TransferredChip({
  ticket,
  counters,
  colleagues,
  className,
}: {
  ticket: Pick<Ticket, 'transferredFrom'>
  counters: readonly CounterView[]
  colleagues: readonly Colleague[]
  className?: string
}) {
  const by = transferredFromName(ticket, counters, colleagues)
  return by === null ? null : <SentChip by={by} className={className} />
}

/** The chip for a known sender ('' when unnamed). Its title carries the whole text, for when the chip is clipped. */
export function SentChip({ by, className }: { by: string; className?: string }) {
  const { say } = useStaffText()
  const text = by ? `${say('counter.transferred')} · ${by}` : say('counter.transferred')
  return (
    <span className={className ? `tag ${className}` : 'tag'} title={text}>
      {text}
    </span>
  )
}

/** The branch's colleagues, for naming who transferred a ticket. Only a desk
 *  operator may read them; anyone else gets [] and the names of signed-in desks. */
export function useColleagues(branchId: string | undefined, permissions: readonly Permission[]): Colleague[] {
  const [colleagues, setColleagues] = useState<Colleague[]>([])
  const may = permissions.includes('counter.operate')
  useEffect(() => {
    if (!branchId || !may) {
      setColleagues([])
      return
    }
    let gone = false
    api
      .colleagues(branchId)
      .then((r) => !gone && setColleagues(r.colleagues ?? []))
      .catch(() => !gone && setColleagues([]))
    return () => {
      gone = true
    }
  }, [branchId, may])
  return colleagues
}
